/**
 * 🗺️ **The map of Iceland** — T189, the World map tab.
 *
 * 🎯 **haugur, 2026-09-11:** *"a map of iceland that you can zoom around in,
 * find places, click them and select teleport to them."*
 *
 * ## 🔍 The close-up — T200, 2026-09-14
 *
 * 🎯 **haugur:** *"i want to be able to zoom much more into the map, we have all
 * the information needed, if i zoom close enough i like to see the streets with
 * names."*
 *
 * ✅ **He was right that the data is there** — 58,266 road segments, 58,243 of
 * them named. 🔴 **What was missing was reach and a limit.** T189 baked ONE
 * close-up into `/api/map`, clipped to a fixed box round `TEST_AREA`, and stopped
 * the zoom at 900 m. So there was street detail in one suburb of Hafnarfjörður
 * and **none anywhere else in Iceland, at any zoom** — which is what he hit.
 *
 * ✅ **The close-up is now asked for BY VIEW**, from `/api/mapdetail`, and the
 * zoom goes to {@link MIN_SPAN}. **One way in**: the old baked box is gone rather
 * than kept alongside.
 *
 * 🔴 **WHETHER NAMES ARE DRAWN IS THE SERVER'S DECISION, NOT THIS FILE'S.** The
 * answer carries `detail: 'all' | 'main'` — every street, or only the main roads
 * because the view is too wide to send them all. **This file reads what it was
 * given.** ⚠️ A second copy of that threshold here is exactly the *second way
 * in* fault this repo has recorded seven times, and it would drift the first
 * time the measurement changed.
 *
 * ## 🔴 What is still honest about the map
 *
 * **All of Iceland is standable since T193**, so every landmark is clickable and
 * {@link insideWorld} asks `WORLD_AREA`. ⚠️ **The bright box is still drawn**
 * — it is where we started and the only place with anything built in it — but
 * it no longer decides whether a place exists.
 *
 * ## The projection, such as it is
 *
 * ISN93 metres straight into the SVG `viewBox`, **with y negated** so north is
 * up — `northingToZ` does the same thing for the world, for the same reason.
 * No reprojection: ISN93 *is* a projection, it is the one the whole game speaks,
 * and converting to anything else would put a second coordinate system into a
 * codebase that has carefully kept one.
 *
 * ## ⚠️ Why the geometry is fetched and not bundled
 *
 * `/api/map` is ~37 kB gzipped and **is fetched the first time this tab is
 * opened, never on the way into the game**. A tester has already called the
 * game laggy; the map must not add a single byte to the path that gets you into
 * the world.
 */

import { CHUNK_SIZE, TEST_AREA, WORLD_AREA, northingToZ, zToNorthing } from '@lofi/shared';
import { LANDMARKS, type Landmark } from './landmarks.ts';
import { placeLabels, textBox, type LabelBox, type LabelCandidate } from './maplabels.ts';

const SVG_NS = 'http://www.w3.org/2000/svg';

/** A ring or line as `[x, y, x, y, …]`, ISN93 metres. */
type FlatRing = number[];
interface NamedShape {
  name: string;
  km2: number;
  rings: FlatRing[];
}
interface MapData {
  extent: [number, number, number, number];
  coast: FlatRing[];
  glaciers: NamedShape[];
  lakes: NamedShape[];
}

/** One street, clipped to a view — see `mapdetail.ts` on the server. */
interface DetailStreet {
  name: string | null;
  kind: string | null;
  lines: FlatRing[];
  label: [number, number] | null;
  metres: number;
}

/** The close-up for one box of ISN93 metres. */
interface MapDetail {
  bounds: [number, number, number, number];
  /** `all` — every street, names worth drawing. `main` — A and B roads only. */
  detail: 'all' | 'main';
  coast: FlatRing[];
  streets: DetailStreet[];
}

export interface WorldMapHandlers {
  /** Where the game server lives, so the map can be fetched. */
  httpBase(): string;
  /** Go to an ISN93 position. @param y **northing**, not world z. */
  onTeleport(x: number, y: number, label: string): void;
  /** Where the player is now, in ISN93 — or `null` before the world loads. */
  playerPosition(): { x: number; y: number } | null;
  /** Everybody else, in ISN93, with their names when known. */
  otherPlayers(): readonly { x: number; y: number; name: string | null }[];
  /**
   * 🏠 **Where the player wakes up**, in ISN93 — or `null` if they have
   * never lit a hearth (T201, protocol 29).
   *
   * ⚠️ **Asked every time the map redraws rather than handed over once**, so a
   * hearth lit while the menu is open moves the button with it. The server
   * sends it on connect and on every change; this reads whatever arrived last.
   */
  homePosition(): { x: number; y: number; z: number } | null;
}

/** The whole country, with a margin so the edges are not flush. */
const COUNTRY_MARGIN = 12000;

/**
 * How far in the zoom goes, as the width of the view in metres.
 *
 * 🎯 **haugur, 2026-09-14:** *"i want to be able to zoom much more into the
 * map."* ⚠️ **It was 900 m** — chosen in T189 to reach the built world, back
 * when the built world was the only thing on the map with any detail in it.
 *
 * **150 m is about a house and its garden**, which is as close as a street name
 * is worth reading.
 */
const MIN_SPAN = 150;

/**
 * Below this many metres across, ask for the close-up.
 *
 * ⚠️ **The server refuses a box over 12,000 m** and decides for itself whether
 * to send every street or only the main roads. This number only says when it is
 * worth asking at all.
 */
const DETAIL_SPAN = 9000;

/**
 * How long to wait after the view stops moving before asking for the close-up.
 *
 * 🔴 **A drag fires a render on every pointer move.** Without this, panning
 * across Reykjavík would open one request per frame at exactly the moment the
 * picture needs the main thread.
 */
const DETAIL_DEBOUNCE_MS = 220;

/**
 * 🔴 **How much MORE than the picture to ask for, each side.**
 *
 * ⚠️ **Not an optimisation — a correctness fix.** A clipped polygon's outline
 * includes the cut itself, so `world_land` clipped to exactly the picture draws
 * **the edge of the box as a straight line across the map.** Over-fetching puts
 * that edge off-picture where it belongs.
 *
 * 🔴 **Found by baking a picture and by nothing else** — three ruled lines
 * across Reykjavík that every test and every element count was happy with.
 *
 * ✅ It also means a small pan reuses what is already drawn.
 */
const DETAIL_MARGIN = 0.1;

export class WorldMap {
  readonly root: HTMLDivElement;

  private readonly handlers: WorldMapHandlers;
  private readonly svg: SVGSVGElement;
  private readonly staticLayer: SVGGElement;
  private readonly detailLayer: SVGGElement;
  private readonly streetLayer: SVGGElement;
  private readonly labelLayer: SVGGElement;
  private readonly liveLayer: SVGGElement;
  private readonly status: HTMLDivElement;
  private readonly readout: HTMLDivElement;
  private readonly homeButton: HTMLButtonElement;

  private data: MapData | null = null;
  private loading = false;

  /**
   * The close-up currently drawn, and the box it was fetched for.
   *
   * 🔴 **Kept while a new one is in flight.** Clearing it first makes the map
   * blink empty on every pan, which reads as a broken map rather than a loading
   * one.
   */
  private detail: MapDetail | null = null;
  private detailTimer: ReturnType<typeof setTimeout> | null = null;
  /** How wide the picture was when {@link detail} was asked for, in metres. */
  private detailSpan: number | null = null;
  /** Rises on every request; a late answer to an old view is dropped. */
  private detailRequest = 0;
  /** True while the status line is showing OUR failure, so only we clear it. */
  private streetError = false;

  /**
   * 🔴 **A fit asked for before the tab had a size, waiting to be re-run.**
   *
   * ⚠️ T189 made `render` SKIP rather than guess when the picture measures
   * 0 × 0 — **but `fitTo` still guessed an aspect ratio**, and a guessed aspect
   * is what decides how far out the country is drawn. So the first view could
   * come up at the wrong zoom and nothing ever asked again. **Remembered and
   * re-run instead.**
   */
  private pendingFit: [number, number, number, number, number] | null = null;

  /** The view, in ISN93: centre and how many metres wide it is. */
  private cx = 0;
  private cy = 0;
  private span = 0;
  /** 🐑⚠️ A place the map is pointing at — a starving flock (T286). Northing, not world z. */
  private marker: { x: number; y: number; label: string } | null = null;

  /** Set while a drag is in progress, in ISN93 metres. */
  private dragFrom: { x: number; y: number; cx: number; cy: number } | null = null;

  constructor(handlers: WorldMapHandlers) {
    this.handlers = handlers;

    this.root = document.createElement('div');
    this.root.className = 'worldmap';

    this.svg = document.createElementNS(SVG_NS, 'svg');
    this.svg.setAttribute('class', 'worldmap-svg');
    this.svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
    this.svg.setAttribute('role', 'img');
    this.svg.setAttribute('aria-label', 'Map of Iceland');

    /* 🔴 Five layers, drawn in this order and never reordered: the country, the
       close-up shore and streets, the street NAMES, the place names, then
       anything that moves. A label under a glacier is a label nobody can read,
       and a street name under a road is a street name nobody can read. */
    this.staticLayer = document.createElementNS(SVG_NS, 'g');
    this.staticLayer.setAttribute('class', 'worldmap-static');
    this.detailLayer = document.createElementNS(SVG_NS, 'g');
    this.streetLayer = document.createElementNS(SVG_NS, 'g');
    this.labelLayer = document.createElementNS(SVG_NS, 'g');
    this.liveLayer = document.createElementNS(SVG_NS, 'g');
    this.svg.append(
      this.staticLayer,
      this.detailLayer,
      this.streetLayer,
      this.labelLayer,
      this.liveLayer,
    );

    this.status = document.createElement('div');
    this.status.className = 'worldmap-status';
    this.status.textContent = 'Loading the map…';

    this.readout = document.createElement('div');
    this.readout.className = 'worldmap-readout';

    const controls = document.createElement('div');
    controls.className = 'worldmap-controls';
    const zoomOut = this.button('−', 'Zoom out', () => this.zoomBy(1.6));
    const zoomIn = this.button('+', 'Zoom in', () => this.zoomBy(1 / 1.6));
    const iceland = this.button('Iceland', 'Show the whole country', () => this.showCountry());
    const world = this.button('The world', 'Show the built world', () => this.showWorld());
    /* 🏠 T201. ⚠️ Its label and whether it works are decided in
       {@link refreshHomeButton}, which runs on every render — because a hearth
       lit while this menu is open must move the button with it. */
    this.homeButton = this.button('Back to home', '', () => this.goHome());
    this.homeButton.classList.add('home');
    controls.append(zoomOut, zoomIn, this.homeButton, iceland, world);

    this.root.append(this.svg, this.status, controls, this.readout);

    this.svg.addEventListener('wheel', (e) => this.onWheel(e), { passive: false });
    this.svg.addEventListener('pointerdown', (e) => this.onPointerDown(e));
    this.svg.addEventListener('pointermove', (e) => this.onPointerMove(e));
    this.svg.addEventListener('pointerup', (e) => this.onPointerUp(e));
    this.svg.addEventListener('pointercancel', () => (this.dragFrom = null));

    /* 🔴 **The map is built inside a hidden tab and therefore has no size.**
       This fires the moment it is shown, and again whenever the window changes
       — which is the only reliable way to draw a picture whose shape depends on
       the box it is in. ⚠️ Without it the first render is skipped and nothing
       ever asks again. */
    new ResizeObserver(() => {
      /* 🔴 A fit that was asked for while the tab was hidden is re-run now that
         there is something to measure — see {@link pendingFit}. */
      const owed = this.pendingFit;
      if (owed !== null && this.viewSize() !== null) {
        this.pendingFit = null;
        this.fitTo(owed[0], owed[1], owed[2], owed[3], owed[4]);
        return;
      }
      this.render();
    }).observe(this.svg);
  }

  private button(label: string, title: string, onClick: () => void): HTMLButtonElement {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'worldmap-button';
    b.textContent = label;
    b.title = title;
    b.setAttribute('aria-label', title);
    b.addEventListener('click', onClick);
    return b;
  }

  /**
   * Fetch the geometry, once.
   *
   * ⚠️ **Called when the tab is first shown, not when the menu is built.** The
   * whole reason this is a fetch rather than a bundle is to stay off the path
   * into the world; building the menu is on that path.
   */
  async show(): Promise<void> {
    this.drawLive();
    if (this.data !== null || this.loading) return;
    this.loading = true;
    try {
      const response = await fetch(`${this.handlers.httpBase()}/api/map`);
      if (!response.ok) throw new Error(`the server answered ${response.status}`);
      this.data = (await response.json()) as MapData;
      this.status.textContent = '';
      this.status.classList.add('gone');
      this.showCountry();
    } catch (err) {
      /* 🔴 Say what went wrong, in words. A blank map and a silent console is
         indistinguishable from a map of a country with nothing in it. */
      this.status.textContent = `The map could not be loaded — ${String(err)}`;
      this.status.classList.add('bad');
    } finally {
      this.loading = false;
    }
  }

  /**
   * 🐑⚠️ **Look at a place, and mark it** — T286, the starving flock.
   *
   * ⚠️ **It does NOT teleport you**, unlike "Back to home": the warning shows
   * you where your sheep are; walking there is yours to do.
   *
   * @param worldZ a WORLD z, converted to a northing here at the boundary,
   *   exactly as {@link goHome} does.
   */
  async focusOn(x: number, worldZ: number, label: string): Promise<void> {
    this.marker = { x, y: zToNorthing(worldZ), label };
    await this.show();
    this.cx = x;
    this.cy = this.marker.y;
    this.span = Math.max(MIN_SPAN, 400);
    this.render();
  }

  /** Redraw the live markers — cheap, and called while the tab is open. */
  tick(): void {
    /* 🏠 On the beat as well as on render: a hearth lit while this tab is
       open changes the button, and nothing else would redraw the map. */
    this.refreshHomeButton();
    if (this.data !== null) this.drawLive();
  }

  // ── the view ──────────────────────────────────────────────────────────────

  /**
   * 🔴 **The size of the picture, or `null` if it has none yet.**
   *
   * ⚠️ **A pane that is `display: none` measures 0 × 0**, and the map is built
   * inside a tab that starts hidden. The first version fell back to a guessed
   * aspect of 0.62 when the width read zero — and **that guess is what cropped
   * the north and south off Iceland and drew the built world as a vertical
   * line.** Nothing threw; it just looked like a map of a different country.
   *
   * 👉 **So a render with no size is SKIPPED, not guessed**, and the
   * `ResizeObserver` runs it again the moment the tab is shown.
   */
  private viewSize(): { w: number; h: number } | null {
    const box = this.svg.getBoundingClientRect();
    return box.width > 1 && box.height > 1 ? { w: box.width, h: box.height } : null;
  }

  /**
   * 🔴 **Fit by whichever side is binding.**
   *
   * ⚠️ Iceland is 514 km across and 364 km tall — **1.41 wide** — and the map
   * pane is wider than that. Fitting by width therefore shows only 211 km of
   * the 364, and **the whole south coast and the Westfjords fall off the
   * picture.** That is what shipped, and haugur said it looked good because
   * what was on screen looked right; you cannot see what is not drawn.
   */
  private fitTo(x0: number, y0: number, x1: number, y1: number, margin: number): void {
    const size = this.viewSize();
    /* 🔴 No size means the tab is still hidden. **Remember the fit and do it
       when there is something to measure** — a guessed aspect decides how far
       out the whole country is drawn, and nothing would ever ask again. */
    if (size === null) {
      this.pendingFit = [x0, y0, x1, y1, margin];
      return;
    }
    const aspect = size.w / size.h;
    this.cx = (x0 + x1) / 2;
    this.cy = (y0 + y1) / 2;
    const width = x1 - x0 + margin * 2;
    const height = y1 - y0 + margin * 2;
    /* The span is a WIDTH, so a too-tall subject needs its height converted
       into the width that would contain it. */
    this.span = Math.max(width, height * aspect);
    this.render();
  }

  private showCountry(): void {
    if (!this.data) return;
    const [x0, y0, x1, y1] = this.data.extent;
    this.fitTo(x0, y0, x1, y1, COUNTRY_MARGIN);
  }

  private showWorld(): void {
    this.fitTo(
      TEST_AREA.minEasting,
      TEST_AREA.minNorthing,
      TEST_AREA.maxEasting,
      TEST_AREA.maxNorthing,
      260,
    );
  }

  private zoomBy(factor: number, at?: { x: number; y: number }): void {
    if (!this.data) return;
    const [x0, y0, x1, y1] = this.data.extent;
    const maxSpan = x1 - x0 + COUNTRY_MARGIN * 2;
    const next = Math.min(maxSpan, Math.max(MIN_SPAN, this.span * factor));
    if (next === this.span) return;

    /* ⚠️ Zoom towards the pointer, not the centre. Zooming to the middle means
       a person hunting for Hafnarfjörður has to pan after every step, which is
       how a map stops being usable at exactly the zoom they wanted. */
    if (at) {
      const k = next / this.span;
      this.cx = at.x + (this.cx - at.x) * k;
      this.cy = at.y + (this.cy - at.y) * k;
    }
    this.span = next;
    this.clampCentre(x0, y0, x1, y1);
    this.render();
  }

  /** Keep the country somewhere on screen — you cannot pan into the Atlantic. */
  private clampCentre(x0: number, y0: number, x1: number, y1: number): void {
    const pad = this.span / 2;
    this.cx = Math.min(x1 + pad / 2, Math.max(x0 - pad / 2, this.cx));
    this.cy = Math.min(y1 + pad / 2, Math.max(y0 - pad / 2, this.cy));
  }

  // ── pointer ───────────────────────────────────────────────────────────────

  /** Where a screen event is, in ISN93. */
  private atEvent(e: PointerEvent | WheelEvent): { x: number; y: number } {
    const box = this.svg.getBoundingClientRect();
    const height = (this.span * box.height) / Math.max(1, box.width);
    return {
      x: this.cx - this.span / 2 + ((e.clientX - box.left) / box.width) * this.span,
      /* ⚠️ Negated: SVG y grows downward, northing grows north. */
      y: this.cy + height / 2 - ((e.clientY - box.top) / box.height) * height,
    };
  }

  private onWheel(e: WheelEvent): void {
    e.preventDefault();
    this.zoomBy(e.deltaY > 0 ? 1.18 : 1 / 1.18, this.atEvent(e));
  }

  private onPointerDown(e: PointerEvent): void {
    const at = this.atEvent(e);
    this.dragFrom = { x: at.x, y: at.y, cx: this.cx, cy: this.cy };
    this.svg.setPointerCapture(e.pointerId);
  }

  private onPointerMove(e: PointerEvent): void {
    const at = this.atEvent(e);
    if (this.dragFrom === null) {
      this.showReadout(at);
      return;
    }
    /* ⚠️ Recomputed from the ORIGINAL centre each move, not accumulated. An
       accumulated delta drifts, because every move changes the mapping the next
       move is measured in. */
    this.cx = this.dragFrom.cx;
    this.cy = this.dragFrom.cy;
    const now = this.atEvent(e);
    this.cx = this.dragFrom.cx + (this.dragFrom.x - now.x);
    this.cy = this.dragFrom.cy + (this.dragFrom.y - now.y);
    if (this.data) {
      const [x0, y0, x1, y1] = this.data.extent;
      this.clampCentre(x0, y0, x1, y1);
    }
    this.render();
  }

  private onPointerUp(e: PointerEvent): void {
    const from = this.dragFrom;
    this.dragFrom = null;
    if (from === null) return;
    const at = this.atEvent(e);
    /* A drag of less than ~0.5 % of the view is a click, not a pan. */
    if (Math.hypot(at.x - from.x, at.y - from.y) > this.span * 0.005) return;
    this.clickAt(at);
  }

  /**
   * 🔴 **Inside the built world, this teleports. Outside, it says why not.**
   */
  private clickAt(at: { x: number; y: number }): void {
    if (!insideWorld(at.x, at.y)) {
      const near = nearestLandmark(at);
      this.status.classList.remove('gone', 'bad');
      this.status.textContent = near
        ? `${near.name} is not built yet — the world is the bright box in Hafnarfjörður.`
        : 'Not built yet — the world is the bright box in Hafnarfjörður.';
      return;
    }
    this.handlers.onTeleport(at.x, at.y, `${at.x.toFixed(0)}, ${at.y.toFixed(0)}`);
  }

  private showReadout(at: { x: number; y: number }): void {
    const inside = insideWorld(at.x, at.y);
    this.readout.textContent = `${at.x.toFixed(0)}, ${at.y.toFixed(0)}${inside ? ' · in the world — click to go' : ''}`;
    this.readout.classList.toggle('in', inside);
  }

  // ── drawing ───────────────────────────────────────────────────────────────

  // ── home ───────────────────────────────────────────────────────

  /**
   * 🏠 **Say what the button can do, every time the map draws.**
   *
   * 🔴 **A button that does nothing is worse than no button**, so with no
   * hearth lit it is greyed and SAYS WHY — the same choice the menu's Snow
   * switch makes, and for the same reason: **a control that is coming is
   * information, and a control that lies is the fault this project keeps
   * paying for.**
   */
  private refreshHomeButton(): void {
    const home = this.handlers.homePosition();
    this.homeButton.disabled = home === null;
    /* 🔴 **`zToNorthing`, not `home.z`.** The hearth is stored in WORLD z,
       which is a negated northing — so the first version offered *your hearth at
       347580, −396315* beside a readout showing positive numbers for the same
       spot. ⚠️ **The teleport below converted and the label did not**, which is
       this repo's oldest fault wearing a tooltip: measuring the thing NEXT to
       the thing. Caught by the baked picture's own report. */
    this.homeButton.title =
      home === null
        ? 'Light a hearth and it becomes your home'
        : `Go to your hearth at ${home.x.toFixed(0)}, ${zToNorthing(home.z).toFixed(0)}`;
    this.homeButton.setAttribute('aria-label', this.homeButton.title);
    this.homeButton.textContent = home === null ? 'No home yet' : 'Back to home';
  }

  /**
   * Go to the hearth.
   *
   * 🔴 **The SAME teleport the map already uses for a click**, which asks
   * `/api/terrain` for the ground and holds you in the air until it arrives.
   * ⚠️ A second path that forgot to do that would drop the player through the
   * world at the one moment they are least expecting it — *a second way in that
   * forgets what the first one does*, recorded seven times here.
   */
  private goHome(): void {
    const home = this.handlers.homePosition();
    /* ⚠️ Re-read rather than trust the button: a hearth can be lit between the
       draw and the click, and a disabled button can be clicked by a script. */
    if (home === null) return;
    /* ⚠️ `home.z` is a WORLD z and this interface speaks northings. Converted
       here, at the boundary, exactly as everywhere else in this file. */
    const northing = zToNorthing(home.z);
    this.handlers.onTeleport(home.x, northing, 'your hearth');
    /* Put the view where you are going, so the map agrees with the world. */
    this.cx = home.x;
    this.cy = northing;
    this.span = Math.max(MIN_SPAN, Math.min(this.span, 600));
    this.render();
  }

  private render(): void {
    /* 🏠 **Before the early returns.** The button has to say something even
       when there is no geometry yet — the map is fetched on first open, and a
       blank button during that second reads as a broken one. */
    this.refreshHomeButton();
    if (!this.data) return;
    const size = this.viewSize();
    /* 🔴 No size yet — the tab is still hidden. The observer will call back. */
    if (size === null) return;
    const height = (this.span * size.h) / size.w;
    /* ⚠️ `-cy - height/2` — the whole map is drawn at NEGATED y so north is up,
       so the top edge of the view is the negation of the highest northing. */
    this.svg.setAttribute(
      'viewBox',
      `${this.cx - this.span / 2} ${-this.cy - height / 2} ${this.span} ${height}`,
    );
    this.drawStatic();
    this.drawDetail();
    /* 🔴 **The PLACE names go down FIRST**, and the street names are handed
       their boxes to keep out of — they are in different layers, and culling
       each set against only itself looks finished and is not.
       ⚠️ **And so do the buttons and the readout**, which sit OVER the map in
       HTML and hide whatever is under them. 🎯 Seen in haugur's own
       screenshot from staging: a street name behind the zoom controls, with
       nothing on screen saying a name was there at all. */
    this.drawStreetNames(size, [...this.drawLabels(), ...this.overlayBoxes()]);
    this.drawLive();
    this.wantDetail();
  }

  // ── the close-up ──────────────────────────────────────────────

  /**
   * Ask the server for the close-up over this view, if we need a new one.
   *
   * ⚠️ **Debounced**, because a drag renders on every pointer move.
   */
  private wantDetail(): void {
    if (this.detailTimer !== null) clearTimeout(this.detailTimer);

    if (this.span > DETAIL_SPAN) {
      /* Zoomed back out past the close-up: drop it, or the shore of one town
         hangs over the country map. */
      if (this.detail !== null) {
        this.detail = null;
        this.detailSpan = null;
        this.detailLayer.replaceChildren();
        this.streetLayer.replaceChildren();
      }
      return;
    }

    this.detailTimer = setTimeout(() => void this.fetchDetail(), DETAIL_DEBOUNCE_MS);
  }

  /** The view as an ISN93 box, `[x0, y0, x1, y1]`. */
  private viewBounds(): [number, number, number, number] | null {
    const size = this.viewSize();
    if (size === null) return null;
    const height = (this.span * size.h) / size.w;
    return [
      this.cx - this.span / 2,
      this.cy - height / 2,
      this.cx + this.span / 2,
      this.cy + height / 2,
    ];
  }

  /**
   * 🔴 **Is what we already have good enough for this view?**
   *
   * Two questions, and both have to be yes:
   *
   * 1. **Is the whole picture inside the box we fetched?** A pan inside the
   *    margin needs nothing new.
   * 2. **Has the zoom stayed put?** ⚠️ Because the SERVER decides whether the
   *    answer carries every street or only the main roads, and it decides on how
   *    wide the picture is. A view that has changed width by more than a tenth
   *    might be owed a different answer — **and this file must not hold a copy
   *    of that threshold to work out whether it is.**
   */
  private detailCovers(want: readonly [number, number, number, number]): boolean {
    const have = this.detail?.bounds;
    if (!have || this.detailSpan === null) return false;
    if (Math.abs(this.span - this.detailSpan) > this.detailSpan * 0.1) return false;
    /* ⚠️ Named rather than indexed by a loop counter — the same reason `path`
       below walks with an iterator: a computed array read trips
       `security/detect-object-injection`, and a four-member tuple has names. */
    const [wx0, wy0, wx1, wy1] = want;
    const [hx0, hy0, hx1, hy1] = have;
    /* The server snaps its bounds to 50 m, so allow that much slack. */
    return wx0 >= hx0 - 50 && wy0 >= hy0 - 50 && wx1 <= hx1 + 50 && wy1 <= hy1 + 50;
  }

  private async fetchDetail(): Promise<void> {
    const want = this.viewBounds();
    if (want === null || this.detailCovers(want)) return;

    const mine = ++this.detailRequest;
    const [x0, y0, x1, y1] = want;
    /* 🔴 Ask for MORE than the picture — see {@link DETAIL_MARGIN}. */
    const padX = (x1 - x0) * DETAIL_MARGIN;
    const padY = (y1 - y0) * DETAIL_MARGIN;
    const query =
      `x0=${Math.round(x0 - padX)}&y0=${Math.round(y0 - padY)}` +
      `&x1=${Math.round(x1 + padX)}&y1=${Math.round(y1 + padY)}` +
      /* ⚠️ How wide the PICTURE is, so the server's every-street rule follows
         what the player sees rather than the margin. */
      `&view=${Math.round(this.span)}`;
    const askedAt = this.span;

    try {
      const response = await fetch(`${this.handlers.httpBase()}/api/mapdetail?${query}`);
      if (!response.ok) throw new Error(`the server answered ${response.status}`);
      const detail = (await response.json()) as MapDetail;
      /* 🔴 A late answer to a view the player has already left is DROPPED. Two
         requests in flight can land in either order, and the older one landing
         last would draw the streets of somewhere else. */
      if (mine !== this.detailRequest) return;
      this.detail = detail;
      this.detailSpan = askedAt;
      if (this.streetError) {
        /* ⚠️ Only ever clear OUR message. The map-wide failure and the
           not-built-yet note share this line, and wiping somebody else's is how
           a real error disappears the moment a pan succeeds. */
        this.streetError = false;
        this.status.textContent = '';
        this.status.classList.remove('bad');
        this.status.classList.add('gone');
      }
      this.render();
    } catch (err) {
      if (mine !== this.detailRequest) return;
      /* ⚠️ Say so. A close-up that silently fails looks exactly like a place
         with no streets in it — and most of Iceland genuinely has none, so
         there is nothing on screen to tell the two apart. */
      this.streetError = true;
      this.status.classList.remove('gone');
      this.status.classList.add('bad');
      this.status.textContent = `The streets could not be loaded — ${String(err)}`;
    }
  }

  /** The close-up shore and the streets. */
  private drawDetail(): void {
    this.detailLayer.replaceChildren();
    const detail = this.detail;
    /* 🔴 **With a close-up on screen, the COUNTRY coast is hidden.**
       ⚠️ It is simplified to 300 m, so at this zoom it draws a second shoreline
       hundreds of metres from the accurate one — **two coasts disagreeing, and
       no way to tell from the picture which is the real one.** ✅ The close-up
       covers the whole view, so nothing is lost by hiding it. */
    this.staticLayer.classList.toggle('detailed', detail !== null);
    if (detail === null) return;

    for (const ring of detail.coast) {
      this.detailLayer.appendChild(path(ring, 'worldmap-localland'));
    }
    for (const street of detail.streets) {
      /* Main roads are drawn heavier, the way they are on any map — and it is
         also the only thing on screen at a wide close-up. */
      const className =
        street.kind === 'A' || street.kind === 'B' ? 'worldmap-road main' : 'worldmap-road';
      for (const line of street.lines) {
        this.detailLayer.appendChild(path(line, className, false));
      }
    }
  }

  /**
   * 🩧 **The street names.**
   *
   * 🔴 **Drawn only when the server said `all`** — at a wider view it sent the
   * main roads alone, and naming those few would read as a country with six
   * streets in it. **The threshold lives on the server; this reads the answer.**
   */
  private drawStreetNames(size: { w: number; h: number }, reserved: LabelBox[]): void {
    this.streetLayer.replaceChildren();
    const detail = this.detail;
    if (detail === null || detail.detail !== 'all') return;

    const perPixel = this.span / size.w;
    const height = perPixel * size.h;
    const left = this.cx - this.span / 2;
    const top = this.cy + height / 2;

    const candidates: LabelCandidate[] = [];
    for (const street of detail.streets) {
      if (street.name === null || street.label === null) continue;
      candidates.push({
        name: street.name,
        x: street.label[0],
        y: street.label[1],
        metres: street.metres,
      });
    }

    /* ⚠️ **11 px, not 11 metres.** An SVG font-size is in user units and the
       user unit here is a metre, so every size on this map is divided by the
       zoom. It is the same conversion `drawLabels` makes. */
    const fontPx = 11;
    const placed = placeLabels(
      candidates,
      (x, y) => ({ px: (x - left) / perPixel, py: (top - y) / perPixel }),
      size,
      fontPx,
      undefined,
      reserved,
    );

    for (const label of placed) {
      const text = document.createElementNS(SVG_NS, 'text');
      text.setAttribute('class', 'worldmap-label street');
      text.setAttribute('x', String(label.x));
      text.setAttribute('y', String(-label.y));
      text.setAttribute('font-size', String(perPixel * fontPx));
      text.textContent = label.name;
      this.streetLayer.appendChild(text);
    }
  }

  /** The country: land, ice, water. Drawn once — the shapes never change. */
  private drawStatic(): void {
    if (!this.data || this.staticLayer.childNodes.length > 0) return;

    for (const ring of this.data.coast) {
      this.staticLayer.appendChild(path(ring, 'worldmap-land'));
    }
    for (const lake of this.data.lakes) {
      for (const ring of lake.rings) this.staticLayer.appendChild(path(ring, 'worldmap-water'));
    }
    for (const glacier of this.data.glaciers) {
      for (const ring of glacier.rings) this.staticLayer.appendChild(path(ring, 'worldmap-ice'));
    }

    /* 🔴 The built world, and it is the brightest thing on the map because it
       is the only part you can stand in. */
    const box = document.createElementNS(SVG_NS, 'rect');
    box.setAttribute('class', 'worldmap-world');
    box.setAttribute('x', String(TEST_AREA.minEasting));
    box.setAttribute('y', String(-TEST_AREA.maxNorthing));
    box.setAttribute('width', String(TEST_AREA.maxEasting - TEST_AREA.minEasting));
    box.setAttribute('height', String(TEST_AREA.maxNorthing - TEST_AREA.minNorthing));
    this.staticLayer.appendChild(box);
  }

  /**
   * The names.
   *
   * 🔴 **Redrawn on every zoom, because which ones belong on screen changes.**
   * ⚠️ **The text is scaled INVERSELY to the zoom** — an SVG `font-size` is in
   * user units, so a 12 px label at country zoom would be 12 *metres* tall and
   * invisible, and at world zoom it would cover Hafnarfjörður.
   */
  private drawLabels(): LabelBox[] {
    this.labelLayer.replaceChildren();
    const taken: LabelBox[] = [];
    if (!this.data) return taken;

    const size = this.viewSize();
    if (size === null) return taken;
    const perPixel = this.span / size.w;
    const height = perPixel * size.h;
    const left = this.cx - this.span / 2;
    const top = this.cy + height / 2;
    /* Show rank 1 always, 2 from ~150 km across, 3 from ~40 km. */
    const limit = this.span > 150000 ? 1 : this.span > 40000 ? 2 : 3;

    for (const mark of LANDMARKS) {
      if (mark.rank > limit) continue;
      const inside = insideWorld(mark.x, mark.y);

      if (mark.kind === 'town') {
        const dot = document.createElementNS(SVG_NS, 'circle');
        dot.setAttribute('class', inside ? 'worldmap-dot in' : 'worldmap-dot');
        dot.setAttribute('cx', String(mark.x));
        dot.setAttribute('cy', String(-mark.y));
        dot.setAttribute('r', String(perPixel * 2.6));
        this.labelLayer.appendChild(dot);
      }

      const text = document.createElementNS(SVG_NS, 'text');
      text.setAttribute(
        'class',
        `worldmap-label ${mark.kind}${inside ? ' in' : ''}${mark.rank === 1 ? ' big' : ''}`,
      );
      /* How far above the point the name sits, in PIXELS — converted to metres
         for the SVG and used as-is for the box. ⚠️ It moves the name UP the
         screen, so in pixel space it subtracts. */
      const liftPx = mark.kind === 'town' ? 6 : 2;
      const fontPx = mark.rank === 1 ? 13 : 11;
      text.setAttribute('x', String(mark.x));
      text.setAttribute('y', String(-mark.y - perPixel * liftPx));
      text.setAttribute('font-size', String(perPixel * fontPx));
      text.textContent = mark.name;
      this.labelLayer.appendChild(text);

      /* 🔴 Report where it went, so the street names can keep clear.
         ⚠️ Only names actually ON the picture are worth reserving — all fifty
         landmarks are drawn at every zoom and the SVG clips the rest, so
         reserving them all would block streets against names nobody can see. */
      const box = textBox(
        (mark.x - left) / perPixel,
        (top - mark.y) / perPixel - liftPx,
        mark.name,
        fontPx,
      );
      if (box.right >= 0 && box.left <= size.w && box.bottom >= 0 && box.top <= size.h) {
        taken.push(box);
      }
    }

    return taken;
  }

  /**
   * 🔴 **Where the HTML controls sit, in the picture's own pixels.**
   *
   * The buttons, the readout and the status note are positioned over the map
   * rather than inside the SVG, so **nothing in the drawing knows they are
   * there** and a street name will happily be drawn underneath one. ⚠️ A label
   * hidden by a button is worse than a dropped one: the street looks unnamed,
   * and the map gives no sign that anything is missing.
   *
   * ⚠️ Measured rather than hard-coded, because the control strip changes
   * width with its labels — *Back to home* and *No home yet* are not the same
   * size, and T201 added a fourth button to a row that already had three.
   */
  private overlayBoxes(): LabelBox[] {
    const frame = this.svg.getBoundingClientRect();
    const boxes: LabelBox[] = [];
    for (const el of this.root.querySelectorAll(
      '.worldmap-controls, .worldmap-readout, .worldmap-status',
    )) {
      /* A status note that has been cleared still occupies a rect of zero. */
      const box = el.getBoundingClientRect();
      if (box.width < 1 || box.height < 1) continue;
      boxes.push({
        left: box.left - frame.left,
        right: box.right - frame.left,
        top: box.top - frame.top,
        bottom: box.bottom - frame.top,
      });
    }
    return boxes;
  }

  /** You, and everybody else. Redrawn every time it is asked for. */
  private drawLive(): void {
    this.liveLayer.replaceChildren();
    const size = this.viewSize();
    if (size === null) return;
    const perPixel = this.span / size.w;

    for (const other of this.handlers.otherPlayers()) {
      const dot = document.createElementNS(SVG_NS, 'circle');
      dot.setAttribute('class', 'worldmap-other');
      dot.setAttribute('cx', String(other.x));
      dot.setAttribute('cy', String(-other.y));
      dot.setAttribute('r', String(perPixel * 3.4));
      this.liveLayer.appendChild(dot);

      if (other.name !== null && this.span <= DETAIL_SPAN) {
        const text = document.createElementNS(SVG_NS, 'text');
        text.setAttribute('class', 'worldmap-label other');
        text.setAttribute('x', String(other.x));
        text.setAttribute('y', String(-other.y - perPixel * 7));
        text.setAttribute('font-size', String(perPixel * 11));
        text.textContent = other.name;
        this.liveLayer.appendChild(text);
      }
    }

    /* 🐑⚠️ T286 — the starving flock, a red ring with its words beside it. */
    if (this.marker !== null) {
      const ring = document.createElementNS(SVG_NS, 'circle');
      ring.setAttribute('class', 'worldmap-starving');
      ring.setAttribute('cx', String(this.marker.x));
      ring.setAttribute('cy', String(-this.marker.y));
      ring.setAttribute('r', String(perPixel * 9));
      ring.setAttribute('stroke-width', String(perPixel * 3));
      this.liveLayer.appendChild(ring);
      const text = document.createElementNS(SVG_NS, 'text');
      text.setAttribute('class', 'worldmap-label starving');
      text.setAttribute('x', String(this.marker.x));
      text.setAttribute('y', String(-this.marker.y - perPixel * 14));
      text.setAttribute('font-size', String(perPixel * 13));
      text.textContent = this.marker.label;
      this.liveLayer.appendChild(text);
    }

    const me = this.handlers.playerPosition();
    if (me === null) return;
    const dot = document.createElementNS(SVG_NS, 'circle');
    dot.setAttribute('class', 'worldmap-me');
    dot.setAttribute('cx', String(me.x));
    dot.setAttribute('cy', String(-me.y));
    dot.setAttribute('r', String(perPixel * 4));
    this.liveLayer.appendChild(dot);
  }
}

// ── helpers ─────────────────────────────────────────────────────────────────

/**
 * ⚠️ The same test `search.ts` and `devtools.ts` use, asked in ISN93.
 *
 * 🔴 **All of Iceland since T193.** It used to be `TEST_AREA`, which is what
 * made every landmark outside the box unclickable. **The box is still drawn** —
 * it is where we started, and the only place with anything built in it — but it
 * is no longer what decides whether a place exists.
 */
function insideWorld(x: number, northing: number): boolean {
  return (
    x >= WORLD_AREA.minEasting &&
    x <= WORLD_AREA.maxEasting &&
    northing >= WORLD_AREA.minNorthing &&
    northing <= WORLD_AREA.maxNorthing
  );
}

function nearestLandmark(at: { x: number; y: number }): Landmark | null {
  let best: Landmark | null = null;
  let bestDistance = Infinity;
  for (const mark of LANDMARKS) {
    const d = Math.hypot(mark.x - at.x, mark.y - at.y);
    if (d < bestDistance) {
      bestDistance = d;
      best = mark;
    }
  }
  /* Only claim a name if the click was near enough to mean it — 25 km. */
  return bestDistance < 25000 ? best : null;
}

/** A flat `[x,y,…]` ring as an SVG path, at negated y. */
function path(flat: FlatRing, className: string, close = true): SVGPathElement {
  const parts: string[] = [];
  /* ⚠️ Walked with an iterator rather than indexed by a computed number — the
     same reason `hud-parts.ts` zips instead of indexing: a variable array read
     trips `security/detect-object-injection`, and the rule is right often
     enough to be worth writing around. */
  let even = true;
  let x = 0;
  let first = true;
  for (const n of flat) {
    if (even) {
      x = n;
    } else {
      parts.push(`${first ? 'M' : 'L'}${x} ${-n}`);
      first = false;
    }
    even = !even;
  }
  if (close) parts.push('Z');
  const el = document.createElementNS(SVG_NS, 'path');
  el.setAttribute('class', className);
  el.setAttribute('d', parts.join(''));
  return el;
}

/** ⚠️ Exported for the world-z boundary, so nothing converts by hand. */
export { northingToZ, zToNorthing, CHUNK_SIZE };
